import { useRenderedBootHumanWait } from '#~/routes/boot-overlay/boot-budget-view.js';
import * as Wizard from '#~/agent/components/wizard/index.js';
import { useManifest } from '#~/agent/hooks.js';
import type { Manifest$Entry } from '#~/agent/index.js';
import {
  INSTALLER_FAILURE_REASONS,
  UNKNOWN_DOWNLOAD_TARGET,
  createDownloadLifecycleTracker,
  resolveDownloadOrigin,
  useAnalytics
} from '#~/analytics/index.js';
import type { InstallerFailureReason } from '#~/analytics/types/properties.js';
import { config } from '#~/config/config.js';
import { Paths } from '#~/routes/index.js';
import { Anchor } from '@studio/design-system/button';
import { Loader } from '@studio/design-system/loader';
import type { Architecture } from '@studio/models/agent';
import { useLatestFn, useLazyRef, useUnmount } from '@studio/react-utils/hooks';
import { toOperatingSystem } from '@studio/react-utils/user-agent';
import type { T_Maybe } from '@tryiris-ai/js-types';
import { isNotEmpty, toString } from '@tryiris-ai/js-types';
import type { ReactElement } from 'react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Icon } from '../../icon.jsx';

/** `<Download>` component. */
export const Download = (): ReactElement => {
  const params = useParams();
  const navigate = useNavigate();
  const manifest = useManifest();
  const [entry, setEntry] = useState<T_Maybe<Manifest$Entry>>();
  const abortRef = useLazyRef<T_Maybe<AbortController>>(() => new AbortController());
  const { track } = useAnalytics();
  const downloadTracker = createDownloadLifecycleTracker(track);

  // ENG-4310 stage-1 failure terminal for the download route. The route is only
  // ever reached from the OS picker with a key the manifest has, so in practice
  // this fires for a STALE ENTRY POINT: a bookmarked or shared
  // /agent/setup/download/:os/:key whose artifact has since been dropped, or a
  // hand-edited one. The picker's own dead end — the far more common way to be
  // told you cannot install — is instrumented in select.tsx.
  //
  // Guarded on env/vendor for the same reason the start emit is: both are
  // required properties of the canonical contract, so an emission missing them
  // is rejected downstream rather than usefully partial. The requested platform
  // falls back to the raw route values, because an unparsed value is the only
  // thing that explains a row from a malformed link.
  const emitInstallerFailed = useLatestFn((reason: InstallerFailureReason) => {
    // `isNotEmpty`, not truthiness and not a bare undefined check: `config` is a
    // lazy Proxy so either value can be missing at runtime despite env's
    // non-optional type, AND the canonical receipt treats '' as a dropped
    // required property — emitting an empty environment would fail the contract
    // instead of recording a partial failure. Same predicate as the picker's
    // emit so the two stage-1 failure sites cannot disagree on what "present"
    // means.
    if (!isNotEmpty(config.env) || !isNotEmpty(config.vendor)) {
      return;
    }

    downloadTracker.trackInstallerFailed({
      installer_failure_reason: reason,
      ...resolveDownloadOrigin(),
      operating_system:
        toOperatingSystem(params.os) ?? toString(params.os) ?? UNKNOWN_DOWNLOAD_TARGET,
      architecture: toString(params.key) ?? UNKNOWN_DOWNLOAD_TARGET,
      environment: config.env,
      vendor: config.vendor
    });
  });

  const lookupEntry = useLatestFn(() => {
    const os = toOperatingSystem(params.os);
    const arch = toString(params.key) as T_Maybe<Architecture>;

    const candidate = os && arch && manifest ? manifest[os][arch] : undefined;

    // Sanity check download entry.
    if (!candidate) {
      // Before ENG-4310 an unsupported platform (every Intel Mac, every Linux
      // user) was bounced back to the OS picker in total silence, so a whole
      // platform could be un-installable with nothing in PostHog. Emit BEFORE
      // the bounce — the component unmounts once the navigation runs.
      emitInstallerFailed(INSTALLER_FAILURE_REASONS.ARTIFACT_UNAVAILABLE);

      queueMicrotask(() => {
        navigate(`/${Paths.agent.setup.select}`);
      });

      return;
    }

    setEntry(candidate);
  });

  const directDownload = useLatestFn(() => {
    if (!entry) {
      return;
    }

    // ENG-4347 stage-1 rename: dual-emits download_app_started + legacy
    // download_electron_app via the domain tracker. Fires here — immediately
    // before the synthetic link.click() — and nowhere else, so the event
    // counts actual download initiations, never page mounts or OS selection.
    // The env/vendor guard predates the rename and is kept as-is: the legacy
    // event's emission conditions must not change during the parity window.
    //
    // The origin is resolved, not hardcoded: this page also renders in the
    // browser, where the download is the user's first (journey stage 1) rather
    // than a re-download from the installed app.
    // This download may never become a first desktop launch, and a machine that
    // cannot run the app can never send a "launch failed" event of its own. So the
    // download→launch gap is not instrumented as a client failure here: it is
    // measured downstream as a NON-CONVERSION — a person with this event and no
    // later boot_started, joined per person. The absence of the launch IS the
    // signal; there is deliberately nothing to emit for the machine that never ran.
    if (config.env && config.vendor) {
      downloadTracker.trackDownloadAppStarted({
        ...resolveDownloadOrigin(),
        environment: config.env,
        vendor: config.vendor
      });
    }

    const link = document.createElement('a');
    link.href = entry.url;
    link.download = entry.fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  });

  // When manifest is loaded, lookup agent entry.
  useEffect(() => {
    if (manifest) {
      lookupEntry();
    }
  }, [manifest]);

  useUnmount(() => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = undefined;
    }
  });

  // Auto-trigger download when entry is available
  useEffect(() => {
    if (entry) {
      directDownload();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entry]);

  useRenderedBootHumanWait(Boolean(entry));

  if (!entry) {
    return <Loader />;
  }

  return (
    <Wizard.Root currentStep={3} totalSteps={3} theme='success'>
      <Icon theme='success' />
      <Wizard.Verbiage
        message='Navigate to your downloads folder to finish installing.'
        title='Downloading...'
      />
      <Wizard.ButtonBox>
        <Anchor
          className='text-primary'
          onClick={(): void => {
            window.open('https://intercom.help/iris-ai/en', '_blank');
          }}
        >
          Need help? Click here
        </Anchor>
      </Wizard.ButtonBox>
    </Wizard.Root>
  );
};
